'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { cn } from 'lib/utils';
import { type IconNames } from 'atoms/Icon/Icons';
import {
  type BloomProps,
  Bloom,
  HeaderSection,
  Icon,
  RichText,
  Wrapper,
} from 'ui';

export type StatsProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  stats: {
    icon?: IconNames;
    stat: string;
    label: string;
    footnote?: string;
  }[];
  bloom?: BloomProps;
};

const Stats = ({
  eyebrow,
  className,
  title,
  intro,
  stats,
  bloom,
}: StatsProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        as="section"
        className={cn('relative flex flex-col gap-20', className)}
      >
        <Bloom {...bloom} />

        <HeaderSection
          content={intro}
          alignment="center"
          {...{ eyebrow, title }}
          className="relative z-2"
        />

        <div className="relative z-2 flex flex-wrap justify-center gap-x-11 gap-y-20 overflow-hidden">
          {stats?.slice(0, 5)?.map((stat, index) => (
            <motion.article
              className={cn(
                'w-57 shrink-0 flex flex-col gap-6 items-center justify-start text-center relative py-2',
                index < stats.length - 1 && [
                  // Horizontal divider for narrow screens - full width, positioned below
                  'after:content-[""] after:absolute after:left-1/2 after:-bottom-10 after:w-screen after:-translate-x-1/2 after:h-px after:bg-clay-50/15 after:max-[35rem]:block after:min-[35rem]:hidden',
                  // Vertical divider for wide screens - positioned to the right
                  'before:min-[35rem]:content-[""] before:min-[35rem]:absolute before:min-[35rem]:right-0 before:min-[35rem]:translate-x-5.5 before:min-[35rem]:top-1 before:min-[35rem]:bottom-1 before:min-[35rem]:w-px before:min-[35rem]:bg-clay-50/15 before:min-[35rem]:block',
                  // Hide dividers on even for 2-col (560-852)
                  'even:before:min-[35rem]:max-[53.25rem]:hidden',
                  // Hide dividers on 3rd for 3-col (852-1144)
                  '[&:nth-child(3n)]:before:min-[53.25rem]:max-[71.5rem]:hidden',
                  // Hide dividers on 4th for 4-col (1144-1436)
                  '[&:nth-child(4n)]:before:min-[71.5rem]:max-[89.75rem]:hidden',
                  // Never show divider on last item
                  'last:before:!hidden last:after:!hidden',
                ]
              )}
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{
                duration: 0.6,
                delay: 0.1 + index * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {!!stat.icon && (
                <div className="flex size-8">
                  <Icon
                    name={stat.icon}
                    className="shrink-0 max-h-8 max-w-8 size-auto fill-athletic-200"
                    aria-hidden="true"
                    role="presentation"
                  />
                </div>
              )}

              <header className="flex flex-col gap-4">
                <h3 className="text-h1 font-light">{stat.stat}</h3>
                <RichText theme="neutral" content={stat.label} />
              </header>

              {!!stat.footnote && (
                <footer className="text-p3 text-blue-300">
                  {stat.footnote}
                </footer>
              )}
            </motion.article>
          ))}
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default Stats;
